/**
 * ToastContextOptimized - 최적화된 Toast Context
 * React.memo와 useCallback을 활용하여 불필요한 리렌더링 방지
 */
import React, { createContext, useContext, useState, useCallback, useMemo, memo } from 'react';
import {
  CheckCircleIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline';

export interface Toast {
  id: string;
  type: 'success' | 'error' | 'warning' | 'info';
  title: string;
  message?: string;
  duration?: number;
}

interface ToastContextType {
  toasts: Toast[];
  addToast: (toast: Omit<Toast, 'id'>) => void;
  removeToast: (id: string) => void;
  showSuccess: (title: string, message?: string) => void;
  showError: (title: string, message?: string) => void;
  showWarning: (title: string, message?: string) => void;
  showInfo: (title: string, message?: string) => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};

// ToastProvider 최적화
export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [toasts, setToasts] = useState<Toast[]>([]);

  // addToast를 useCallback으로 메모이제이션
  const addToast = useCallback((toast: Omit<Toast, 'id'>) => {
    const id = Math.random().toString(36).substr(2, 9);
    const newToast = { ...toast, id };

    setToasts((prevToasts) => [...prevToasts, newToast]);

    // 자동 제거
    const duration = toast.duration || 3000;
    setTimeout(() => {
      setToasts((prevToasts) => prevToasts.filter((t) => t.id !== id));
    }, duration);
  }, []);

  // removeToast를 useCallback으로 메모이제이션
  const removeToast = useCallback((id: string) => {
    setToasts((prevToasts) => prevToasts.filter((toast) => toast.id !== id));
  }, []);

  // 헬퍼 함수들 메모이제이션
  const showSuccess = useCallback(
    (title: string, message?: string) => {
      addToast({ type: 'success', title, message });
    },
    [addToast]
  );

  const showError = useCallback(
    (title: string, message?: string) => {
      addToast({ type: 'error', title, message });
    },
    [addToast]
  );

  const showWarning = useCallback(
    (title: string, message?: string) => {
      addToast({ type: 'warning', title, message });
    },
    [addToast]
  );

  const showInfo = useCallback(
    (title: string, message?: string) => {
      addToast({ type: 'info', title, message });
    },
    [addToast]
  );

  // Context value를 useMemo로 메모이제이션
  const contextValue = useMemo(
    () => ({
      toasts,
      addToast,
      removeToast,
      showSuccess,
      showError,
      showWarning,
      showInfo,
    }),
    [toasts, addToast, removeToast, showSuccess, showError, showWarning, showInfo]
  );

  return (
    <ToastContext.Provider value={contextValue}>
      {children}
      <ToastContainer toasts={toasts} removeToast={removeToast} />
    </ToastContext.Provider>
  );
};

// ToastContainer를 memo로 최적화
interface ToastContainerProps {
  toasts: Toast[];
  removeToast: (id: string) => void;
}

const ToastContainer = memo<ToastContainerProps>(({ toasts, removeToast }) => {
  if (toasts.length === 0) return null;

  return (
    <div className="fixed top-4 right-4 z-50 space-y-2">
      {toasts.map((toast) => (
        <ToastItem key={toast.id} toast={toast} onRemove={removeToast} />
      ))}
    </div>
  );
});

ToastContainer.displayName = 'ToastContainer';

// ToastItem을 memo로 최적화
interface ToastItemProps {
  toast: Toast;
  onRemove: (id: string) => void;
}

const ToastItem = memo<ToastItemProps>(({ toast, onRemove }) => {
  // 아이콘 선택 함수 - 컴포넌트 내부에서 정의하여 불필요한 재생성 방지
  const getIcon = () => {
    const iconClass = "h-5 w-5";
    switch (toast.type) {
      case 'success':
        return <CheckCircleIcon className={`${iconClass} text-green-600`} />;
      case 'error':
        return <XCircleIcon className={`${iconClass} text-red-600`} />;
      case 'warning':
        return <ExclamationTriangleIcon className={`${iconClass} text-yellow-600`} />;
      case 'info':
        return <InformationCircleIcon className={`${iconClass} text-blue-600`} />;
      default:
        return <InformationCircleIcon className={`${iconClass} text-gray-600`} />;
    }
  };

  // 스타일 선택 함수
  const getStyles = () => {
    const base = {
      success: { bg: 'bg-green-50 border-green-200', text: 'text-green-800' },
      error: { bg: 'bg-red-50 border-red-200', text: 'text-red-800' },
      warning: { bg: 'bg-yellow-50 border-yellow-200', text: 'text-yellow-800' },
      info: { bg: 'bg-blue-50 border-blue-200', text: 'text-blue-800' },
    };
    
    return base[toast.type] || { bg: 'bg-gray-50 border-gray-200', text: 'text-gray-800' };
  };

  const styles = getStyles();
  
  // 클릭 핸들러 메모이제이션
  const handleRemove = useCallback(() => {
    onRemove(toast.id);
  }, [onRemove, toast.id]);

  return (
    <div className={`${styles.bg} border rounded-lg p-4 shadow-lg max-w-sm animate-slide-in-right`}>
      <div className="flex items-start">
        <div className="flex-shrink-0">{getIcon()}</div>
        <div className="ml-3 flex-1">
          <p className={`text-sm font-medium ${styles.text}`}>{toast.title}</p>
          {toast.message && (
            <p className={`mt-1 text-sm ${styles.text} opacity-80`}>{toast.message}</p>
          )}
        </div>
        <button
          onClick={handleRemove}
          className={`ml-4 flex-shrink-0 p-1 rounded-md hover:bg-white hover:bg-opacity-20 transition-colors ${styles.text}`}
          aria-label="닫기"
        >
          <XCircleIcon className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
});

ToastItem.displayName = 'ToastItem';

export default ToastProvider;